import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Book, History, Atom, Heart, Star, Brain, Calculator, Lightbulb } from 'lucide-react';

export const InfoPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-rose-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16">
        <div className="container max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-amber-200 rounded-full px-4 py-2 mb-6">
            <Book className="h-4 w-4 text-amber-600" />
            <span className="body-small text-amber-800 font-mono uppercase tracking-wider">
              Info Numerologi
            </span>
          </div>
          
          <h1 className="heading-hero mb-6">
            Memahami<br />
            Numerologi Modern
          </h1>
          
          <p className="body-large text-gray-600 mb-8 max-w-2xl mx-auto">
            Pelajari ilmu kuno yang memadukan wisdom tradisional dengan pendekatan sains modern 
            untuk memahami diri Anda lebih dalam.
          </p>
        </div>
      </section>

      {/* What is Numerology */}
      <section className="pb-16">
        <div className="container max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Calculator className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="heading-1">Apa itu Numerologi?</h2>
            </div>

            <div className="space-y-6">
              <p className="body-large text-gray-700 leading-relaxed">
                Numerologi merupakan salah satu metode divinasi yang dapat dijadikan sebagai petunjuk arah atau alat 
                untuk refleksi diri yang dilihat dari angka tanggal lahir untuk melihat gambaran kehidupan manusia 
                seperti kepribadian, potensi unik dalam diri seperti bakat maupun sisi gelap seseorang.
              </p>
              <p className="body-large text-gray-700 leading-relaxed">
                Tanggal lahir bukan hanya sekedar angka. Setiap angka mengandung makna tersembunyi. Dari tanggal lahir, 
                dapat diidentifikasi kekuatan maupun kelemahan seseorang, membantu membuat keputusan tepat dalam hidup 
                seperti pemilihan karir yang cocok, hubungan dan waktu yang tepat untuk memulai proyek baru, meningkatkan 
                hubungan antar pasangan, keluarga, relasi bisnis dan sebagainya. Anda juga dapat melihat peluang dan 
                tantangan kehidupan dan siklus kehidupan tahunan.
              </p>
              <p className="body-large text-gray-700 leading-relaxed">
                Selain dari tanggal lahir, Anda juga dapat mengetahui tujuan hidup dan pelajaran karma dari nama 
                lengkap lahir Anda. Tidak ada yang kebetulan di dunia ini. Kita semua membawa tujuan hidup dan 
                pelajaran karma untuk kita selesaikan di kehidupan saat ini.
              </p>
              <p className="body-large text-gray-700 leading-relaxed">
                Numerologi dapat membantu dalam memahami diri sendiri. Bisa juga ditambah metode divinasi lainnya 
                seperti Astrologi, Human Design, BaZi, dsb. Namun, memerlukan tambahan jam lahir dan kota kelahiran 
                yang tepat untuk melengkapi hasil pembacaan atau semakin detail. Apabila Anda tidak mengingat jam 
                lahir atau merahasiakan jam lahir Anda, Numerologi cukup membantu Anda untuk mengenal diri lebih dalam.
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Star className="h-6 w-6 text-purple-600" />
                    <h3 className="heading-3 text-purple-800">Prinsip Dasar</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className="body-medium text-purple-700">• Setiap angka memiliki getaran energi unik</li>
                    <li className="body-medium text-purple-700">• Tanggal lahir mencerminkan blueprint spiritual</li>
                    <li className="body-medium text-purple-700">• Nama memberikan petunjuk kepribadian</li>
                    <li className="body-medium text-purple-700">• Pola angka mengungkap potensi tersembunyi</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Lightbulb className="h-6 w-6 text-green-600" />
                    <h3 className="heading-3 text-green-800">Manfaat Praktis</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className="body-medium text-green-700">• Memahami kekuatan dan kelemahan diri</li>
                    <li className="body-medium text-green-700">• Membuat keputusan yang lebih tepat</li>
                    <li className="body-medium text-green-700">• Meningkatkan hubungan interpersonal</li>
                    <li className="body-medium text-green-700">• Menemukan tujuan hidup yang bermakna</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* History Section */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <History className="h-6 w-6 text-amber-600" />
              </div>
              <h2 className="heading-1">Sejarah & Perkembangan</h2>
            </div>

            <div className="space-y-8">
              <div className="relative space-y-8">      
                  {/* ITEM 1 */}
                  <div className="flex gap-6 relative before:content-[''] before:absolute before:left-[23px] before:top-12 before:bottom-[-2rem] before:w-[3px] before:bg-gradient-to-b before:from-amber-300 before:to-amber-600 before:opacity-80 last:before:hidden">
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-md">
                      <span className="text-sm font-bold text-amber-800">1</span>
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Peradaban Kuno Mesir dan Babilonia (sekitar 4000–3000 SM)</h3>
                      <p className="body-medium text-gray-700">
                        Di Mesir kuno, angka dipercaya memiliki daya magis dan simbolis, terutama dalam arsitektur (misalnya piramida) dan ritual keagamaan. <br />
                        Sementara di Babilonia, angka digunakan dalam ramalan dan astrologi, misalnya perhitungan posisi planet dan nasib seseorang.
                      </p>
                    </div>
                  </div>

                  {/* ITEM 2 */}
                  <div className="flex gap-6 relative before:content-[''] before:absolute before:left-[23px] before:top-12 before:bottom-[-2rem] before:w-[3px] before:bg-gradient-to-b before:from-amber-300 before:to-amber-600 before:opacity-80 last:before:hidden">
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-md">
                      <span className="text-sm font-bold text-amber-800">2</span>
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">India Kuno (Veda, sekitar 2000 SM)</h3>
                      <p className="body-medium text-gray-700">
                        Dalam ajaran Veda dan Jyotish (astrologi Hindu), angka memiliki hubungan dengan karma, planet, dan kehidupan spiritual. <br />
                        Angka 1–9 dikaitkan dengan planet: misalnya 1 = Matahari, 2 = Bulan, 3 = Jupiter, dll. <br />
                        Tradisi ini berkembang menjadi numerologi India (Chaldean numerology) yang masih digunakan sampai sekarang.
                      </p>
                    </div>
                  </div>
                  
                  {/* ITEM 3 */}
                  <div className="flex gap-6 relative before:content-[''] before:absolute before:left-[23px] before:top-12 before:bottom-[-2rem] before:w-[3px] before:bg-gradient-to-b before:from-amber-300 before:to-amber-600 before:opacity-80 last:before:hidden">
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-md">
                      <span className="text-sm font-bold text-amber-800">3</span>
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Tiongkok Kuno (sekitar 2000 SM)</h3>
                      <p className="body-medium text-gray-700">
                        Numerologi muncul melalui Feng Shui dan I Ching (Kitab Perubahan). Angka dianggap membawa keberuntungan atau kesialan.
                        <br />Contoh: angka 8 melambangkan kemakmuran (karena pengucapannya mirip kata “makmur”), sedangkan 4 dianggap sial (mirip bunyi kata “mati”).
                      </p>
                    </div>
                  </div>
                  
                  {/* ITEM 4 */}
                  <div className="flex gap-6 relative before:content-[''] before:absolute before:left-[23px] before:top-12 before:bottom-[-2rem] before:w-[3px] before:bg-gradient-to-b before:from-amber-300 before:to-amber-600 before:opacity-80 last:before:hidden">
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-md">
                      <span className="text-sm font-bold text-amber-800">4</span>
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Yunani Kuno dan Pythagoras (sekitar 600 SM)</h3>
                      <p className="body-medium text-gray-700">
                        Ini adalah awal dari numerologi Barat modern. <br />
                        Pythagoras, seorang filsuf dan matematikawan Yunani, percaya bahwa semua hal di alam semesta dapat dijelaskan melalui angka. <br />
                        Ia menciptakan sistem Pythagorean Numerology, yang menjadi dasar bagi banyak sistem numerologi modern. <br />
                        Dalam sistemnya, huruf-huruf alfabet diubah menjadi angka (A=1, B=2, …, I=9, lalu kembali ke 1), dan dari situ dihitung Life Path Number, Destiny Number, dll.                      </p>
                    </div>
                  </div>

                  {/* ITEM 5 */}
                  <div className="flex gap-6 relative before:content-[''] before:absolute before:left-[23px] before:top-12 before:bottom-[-2rem] before:w-[3px] before:bg-gradient-to-b before:from-amber-300 before:to-amber-600 before:opacity-80 last:before:hidden">
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-md">
                      <span className="text-sm font-bold text-amber-800">5</span>
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Masa Romawi & Abad Pertengahan (± 100 M – 1500 M)</h3>
                      <p className="body-medium text-gray-700">
                        Numerologi menyatu dengan mistisisme Kristen dan Kabbalah Yahudi. <br />
                        Dalam Kabbalah, huruf Ibrani dianggap memiliki nilai numerik, dan kombinasi angka-huruf digunakan untuk menafsirkan teks suci (misalnya Gematria). <br />
                        Gereja Katolik awal menolak numerologi karena dianggap “okultisme”, tetapi beberapa biarawan tetap mempelajarinya secara rahasia.
                      </p>
                    </div>
                  </div>

                  {/* ITEM 6 */}
                  <div className="flex gap-6 relative before:content-[''] before:absolute before:left-[23px] before:top-12 before:bottom-[-2rem] before:w-[3px] before:bg-gradient-to-b before:from-amber-300 before:to-amber-600 before:opacity-80 last:before:hidden">
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-md">
                      <span className="text-sm font-bold text-amber-800">6</span>
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Zaman Modern (Abad 19–20)</h3>
                      <p className="body-medium text-gray-700">
                        Numerologi kembali populer melalui gerakan spiritualisme dan esoterik di Eropa dan Amerika. <br />
                        Tokoh-tokoh seperti L. Dow Balliett dan Florence Campbell (awal 1900-an) memperkenalkan sistem numerologi modern yang masih digunakan sampai sekarang (berbasis Pythagorean). <br />
                        Di abad ke-20, Cheiro (William John Warner), seorang mistikus Irlandia, mempopulerkan numerologi Chaldean yang berasal dari India kuno.
                      </p>
                    </div>
                  </div>
                  
                  {/* ITEM 7 */}
                  <div className="flex gap-6 relative before:content-[''] before:absolute before:left-[23px] before:top-12 before:bottom-[-2rem] before:w-[3px] before:bg-gradient-to-b before:from-amber-300 before:to-amber-600 before:opacity-80 last:before:hidden">
                    <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-md">
                      <span className="text-sm font-bold text-amber-800">7</span>
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Era Modern dan Digital</h3>
                      <p className="body-medium text-gray-700">
                        Sekarang numerologi digunakan tidak hanya untuk spiritualitas, tapi juga: Self-discovery (pengembangan diri), 
                        Personality profiling (profil karakter), Astrologi modern dan coaching spiritual.
                      </p>
                    </div>
                  </div>

              </div>
            </div>
          </div>

          {/* Science & Spirituality */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Atom className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="heading-1">Numerologi: Sains & Spiritual</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Scientific Perspective */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Brain className="h-6 w-6 text-blue-600" />
                  <h3 className="heading-2 text-blue-800">Perspektif Sains</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Pola Matematika</h4>
                    <p className="body-small text-blue-700">
                      Numerologi menggunakan sistem matematika untuk menganalisis pola. 
                      Penelitian menunjukkan bahwa manusia cenderung merespons pola angka 
                      tertentu secara psikologis.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Psikologi Kognitif</h4>
                    <p className="body-small text-blue-700">
                      Analisis numerologi dapat membantu self-reflection dan introspeksi, 
                      yang terbukti bermanfaat dalam psikologi kognitif dan pengembangan diri.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Statistik & Probabilitas</h4>
                    <p className="body-small text-blue-700">
                      Beberapa aspek numerologi menggunakan analisis statistik untuk 
                      mengidentifikasi korelasi antara tanggal lahir dan traits kepribadian.
                    </p>
                  </div>
                </div>
              </div>

              {/* Spiritual Perspective */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Heart className="h-6 w-6 text-rose-600" />
                  <h3 className="heading-2 text-rose-800">Perspektif Spiritual</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                    <h4 className="font-semibold text-rose-800 mb-2">Energi Vibrational</h4>
                    <p className="body-small text-rose-700">
                      Setiap angka dipercaya memiliki frekuensi energi unik yang memengaruhi 
                      aspek spiritual dan emosional manusia.
                    </p>
                  </div>
                  
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                    <h4 className="font-semibold text-rose-800 mb-2">Sinkronisitas</h4>
                    <p className="body-small text-rose-700">
                      Numerologi mengakui konsep sinkronisitas - bahwa tidak ada kebetulan 
                      dan semua angka dalam hidup memiliki makna spiritual.
                    </p>
                  </div>
                  
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                    <h4 className="font-semibold text-rose-800 mb-2">Pertumbuhan Jiwa</h4>
                    <p className="body-small text-rose-700">
                      Angka-angka personal dipandang sebagai petunjuk perjalanan jiwa dan 
                      lessons yang perlu dipelajari dalam kehidupan ini.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl">
              <h4 className="font-semibold text-purple-800 mb-3">💫 Keseimbangan Modern</h4>
              <p className="body-medium text-purple-700">
                Kami mengintegrasikan kedua perspektif ini - menggunakan metodologi 
                yang terstruktur (sains) untuk memberikan wawasan yang bermakna secara 
                spiritual, menciptakan pendekatan numerologi yang holistik dan relevan 
                untuk kehidupan modern.
              </p>
            </div>
          </div>

          {/* Why Know Your Numbers */}
          <div className="bg-gradient-to-br from-amber-100 via-rose-100 to-purple-100 rounded-2xl border border-amber-200 p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="heading-1 mb-4">Mengapa Perlu Tahu Angka Numerologi Anda?</h2>
              <p className="body-large text-gray-700 max-w-2xl mx-auto">
                Memahami angka numerologi pribadi memberikan keuntungan praktis dalam 
                berbagai aspek kehidupan
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Brain className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="heading-3 mb-3">Pemahaman Diri</h3>
                <p className="body-small text-gray-700">
                  Kenali kekuatan, kelemahan, dan potensi tersembunyi untuk 
                  pengembangan diri yang lebih terarah
                </p>
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="heading-3 mb-3">Hubungan Harmonis</h3>
                <p className="body-small text-gray-700">
                  Pahami kompatibilitas dengan orang lain untuk membangun 
                  hubungan yang lebih bermakna
                </p>
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="heading-3 mb-3">Karir & Tujuan</h3>
                <p className="body-small text-gray-700">
                  Temukan jalur karir dan tujuan hidup yang selaras dengan 
                  kepribadian dan bakat alami
                </p>
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="heading-3 mb-3">Pengambilan Keputusan</h3>
                <p className="body-small text-gray-700">
                  Buat keputusan yang lebih tepat dengan memahami timing 
                  dan energi personal yang optimal
                </p>
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calculator className="h-6 w-6 text-rose-600" />
                </div>
                <h3 className="heading-3 mb-3">Pola Hidup</h3>
                <p className="body-small text-gray-700">
                  Identifikasi pola berulang dalam hidup dan cara 
                  mengoptimalkan atau mengubahnya
                </p>
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Book className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="heading-3 mb-3">Pertumbuhan Spiritual</h3>
                <p className="body-small text-gray-700">
                  Accelerate perjalanan spiritual dengan memahami 
                  lessons dan misi jiwa Anda
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <div className="bg-white/90 rounded-xl p-6 max-w-2xl mx-auto">
                <p className="body-medium text-gray-700 italic">
                  “Seseorang yang mengenal dalam dirinya lebih dalam, maka akan mampu mengenal Tuhan-nya karena setiap manusia 
                  atau setiap makhluk adalah bagian dari Tuhan itu sendiri. 
                  Semakin mengenal siapa dirimu, semakin jelas arah ke jalan pulang ke dalam diri sendiri.”	
                </p>
                <br />
                <p className="body-medium text-gray-700 italic">
                  "Angka-angka dalam hidup Anda bukan kebetulan - mereka adalah kunci 
                  untuk memahami siapa Anda sebenarnya dan ke mana Anda menuju. 
                  Mulai perjalanan penemuan diri Anda hari ini."
                </p>
                <p className="body-small text-amber-700 font-semibold mt-3">— Desy Clare</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InfoPage;